import { Component, OnInit, OnDestroy, signal, DestroyRef, inject, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonModule } from 'primeng/button';
import { SystemDocumentationService } from '@6grain/masofa-monolith-api-client/common/api/systemDocumentation.service';
import { SystemDocumentationBlockDto, PublicSystemDocumentationDto } from '@6grain/masofa-monolith-api-client/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '@root/src/environments/environment';
import { MarkdownPipe } from '@app/utils/pipes/markdown-pipe';

@Component({
  selector: 'app-documentation-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ProgressSpinnerModule,
    ButtonModule,
    MarkdownPipe
  ],
  templateUrl: './documentation-page.html',
  styleUrl: './documentation-page.scss'
})
export class DocumentationPageComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly systemDocumentationService = inject(SystemDocumentationService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);

  @ViewChild('markdownContent', { static: false }) markdownContentRef?: ElementRef<HTMLElement>;

  readonly loading = signal<boolean>(true);
  readonly loadingDocument = signal<boolean>(false);
  readonly blocks = signal<SystemDocumentationBlockDto[]>([]);
  readonly selectedDocument = signal<PublicSystemDocumentationDto | null>(null);
  readonly fileContent = signal<string | null>(null);
  readonly fileType = signal<string | null>(null);
  readonly fileUrl = signal<string | null>(null);

  private currentBlobUrl: string | null = null;
  readonly expandedBlocks = signal<Set<string>>(new Set());
  readonly expandedChildBlocks = signal<Set<string>>(new Set());

   ngOnDestroy(): void {
     // Free blob URL when destroying component
     if (this.currentBlobUrl) {
       window.URL.revokeObjectURL(this.currentBlobUrl);
     }
   }

  ngOnInit(): void {
    this.loadDocumentation();

    // Подписываемся на изменения языка
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Если документ уже выбран, перезагружаем файл для нового языка
        const selectedDoc = this.selectedDocument();
        if (selectedDoc) {
          this.selectDocument(selectedDoc);
        }
      });
  }

  ngAfterViewInit(): void {
    // Добавляем обработчик для якорных ссылок после инициализации
    setTimeout(() => {
      this.setupAnchorLinks();
    }, 100);
  }

  private setupAnchorLinks(): void {
    if (!this.markdownContentRef?.nativeElement) return;

    const markdownContainer = this.markdownContentRef.nativeElement;
    const anchorLinks = markdownContainer.querySelectorAll('a.anchor-link');

    anchorLinks.forEach((link: Element) => {
      const anchor = link as HTMLAnchorElement;
      const href = anchor.getAttribute('href');

       if (href && href.startsWith('#')) {
         anchor.addEventListener('click', (e: Event) => {
           e.preventDefault();
           e.stopPropagation();

           const targetId = href.substring(1); // Remove # from the beginning
           const targetElement = markdownContainer.querySelector(`#${targetId}, [id="${targetId}"]`);

           if (targetElement) {
             targetElement.scrollIntoView({
               behavior: 'smooth',
               block: 'start'
             });

             // Highlight target element
             targetElement.classList.add('anchor-target-highlight');
             setTimeout(() => {
               targetElement.classList.remove('anchor-target-highlight');
             }, 2000);
           }
         });
       }
    });
  }

  loadDocumentation(): void {
    this.loading.set(true);

    this.systemDocumentationService.commonSystemDocumentationGetPublicByBlocksGet()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.blocks.set(response || []);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading documentation:', error);
          this.loading.set(false);
        }
      });
  }

   getNameForLanguage(item: any): string {
     if (!item?.names) return '';

     const currentLang = this.translateService.currentLang || '';

     // Extended mapping for all possible language formats
     const langMap: Record<string, string> = {
       'en': 'en-US',
       'en-us': 'en-US',
       'en_us': 'en-US',
       'en-US': 'en-US',
       'ru': 'ru-RU',
       'ru-ru': 'ru-RU',
       'ru_ru': 'ru-RU',
       'ru-RU': 'ru-RU',
       'uz': 'uz-Latn-UZ',
       'uz-latn': 'uz-Latn-UZ',
       'uz_latn': 'uz-Latn-UZ',
       'uz-latn-uz': 'uz-Latn-UZ',
       'uz_latn_uz': 'uz-Latn-UZ',
       'uz-Latn-UZ': 'uz-Latn-UZ',
       'uz-LATN-UZ': 'uz-Latn-UZ'
     };

     // Normalize current language
     const normalizedLang = currentLang?.toLowerCase() || '';

     // First check for exact match (case-sensitive)
     let langCode = langMap[currentLang] || langMap[normalizedLang] || '';

     // If not found in map, check if key exists directly in names
     if (!langCode && currentLang && item.names[currentLang]) {
       langCode = currentLang;
     }

     // If still not found, try normalized variant
     if (!langCode && normalizedLang) {
       // Check all possible keys in names
       const availableKeys = Object.keys(item.names);
       const matchingKey = availableKeys.find(key => key.toLowerCase() === normalizedLang || key.toLowerCase().includes(normalizedLang));
       if (matchingKey) {
         langCode = matchingKey;
       }
     }

     // Use found language code or return in priority order (first uz-Latn-UZ)
     if (langCode && item.names[langCode]) {
       return item.names[langCode];
     }

     // Fallback: first Uzbek, then English, then Russian, then any available value
     return item.names['uz-Latn-UZ'] || item.names['en-US'] || item.names['ru-RU'] || Object.values(item.names)[0] || '';
   }

   selectDocument(document: PublicSystemDocumentationDto): void {
     // Set loading state
     this.loadingDocument.set(true);

     // Determine current language
     const langMap: Record<string, string> = {
       'en': 'en-US',
       'en-us': 'en-US',
       'en-US': 'en-US',
       'ru': 'ru-RU',
       'ru-ru': 'ru-RU',
       'ru-RU': 'ru-RU',
       'uz': 'uz-Latn-UZ',
       'uz-latn': 'uz-Latn-UZ',
       'uz-latn-uz': 'uz-Latn-UZ',
       'uz-Latn-UZ': 'uz-Latn-UZ'
     };

     const currentLang = this.translateService.currentLang || '';
     const normalizedLang = currentLang.toLowerCase();
     const langCode = langMap[currentLang] || langMap[normalizedLang] || 'uz-Latn-UZ';

     // Check if file exists for current language
     // @ts-ignore
     const fileStorageIds = document.fileStorageIds as { [key: string]: string | null | undefined } | undefined;
     const fileStorageId = fileStorageIds?.[langCode] ||
                          fileStorageIds?.['uz-Latn-UZ'] ||
                          fileStorageIds?.['en-US'] ||
                          fileStorageIds?.['ru-RU'];

     if (!fileStorageId) {
       // Free blob URL during cleanup
       if (this.currentBlobUrl) {
         window.URL.revokeObjectURL(this.currentBlobUrl);
         this.currentBlobUrl = null;
       }
       this.selectedDocument.set(null);
       this.fileContent.set(null);
       this.fileType.set(null);
       this.fileUrl.set(null);
       this.loadingDocument.set(false);
       return;
     }

     this.selectedDocument.set(document);

     // Determine file type by extension
     const fileName = document.fileName || '';
     const extension = fileName.split('.').pop()?.toLowerCase() || '';

     this.fileType.set(extension);

     // If it's markdown or text file, load content
     if (extension === 'md' || extension === 'txt') {
       if (document.id) {
         this.loadTextFile(document.id, langCode);
       }
       this.fileUrl.set(null);
     } else if (document.id) {
       // For PDF and images load file via API client and create blob URL
       this.loadFileForPreview(document.id, langCode);
     } else {
       this.fileContent.set(null);
       this.fileUrl.set(null);
       this.loadingDocument.set(false);
     }

     // After loading content setup anchor links
     setTimeout(() => {
       this.setupAnchorLinks();
     }, 200);
   }

   loadFileForPreview(documentationId: string, language?: string): void {
     // Free previous blob URL if it exists
     if (this.currentBlobUrl) {
       window.URL.revokeObjectURL(this.currentBlobUrl);
       this.currentBlobUrl = null;
     }

    // Определяем язык для запроса
    if (!language) {
      const langMap: Record<string, string> = {
        'en': 'en-US',
        'en-us': 'en-US',
        'en-US': 'en-US',
        'ru': 'ru-RU',
        'ru-ru': 'ru-RU',
        'ru-RU': 'ru-RU',
        'uz': 'uz-Latn-UZ',
        'uz-latn': 'uz-Latn-UZ',
        'uz-latn-uz': 'uz-Latn-UZ',
        'uz-Latn-UZ': 'uz-Latn-UZ'
      };
      const currentLang = this.translateService.currentLang || '';
      const normalizedLang = currentLang.toLowerCase();
      language = langMap[currentLang] || langMap[normalizedLang] || 'uz-Latn-UZ';
    }

    const apiUrl = `${environment.api}/common/SystemDocumentation/GetPublicFile/${documentationId}?language=${language}`;

    this.http.get(apiUrl, { responseType: 'blob' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob: Blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          this.currentBlobUrl = blobUrl;
          this.fileUrl.set(blobUrl);
          this.loadingDocument.set(false);
        },
        error: (error) => {
          console.error('Error loading file for preview:', error);
          this.fileUrl.set(null);
          this.loadingDocument.set(false);
        }
      });
  }

  loadTextFile(documentationId: string, language?: string): void {
    // Определяем язык для запроса
    if (!language) {
      const langMap: Record<string, string> = {
        'en': 'en-US',
        'en-us': 'en-US',
        'en-US': 'en-US',
        'ru': 'ru-RU',
        'ru-ru': 'ru-RU',
        'ru-RU': 'ru-RU',
        'uz': 'uz-Latn-UZ',
        'uz-latn': 'uz-Latn-UZ',
        'uz-latn-uz': 'uz-Latn-UZ',
        'uz-Latn-UZ': 'uz-Latn-UZ'
      };
      const currentLang = this.translateService.currentLang || '';
      const normalizedLang = currentLang.toLowerCase();
      language = langMap[currentLang] || langMap[normalizedLang] || 'uz-Latn-UZ';
    }

    const apiUrl = `${environment.api}/common/SystemDocumentation/GetPublicFile/${documentationId}?language=${language}`;

    this.http.get(apiUrl, { responseType: 'text' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (text: string) => {
          this.fileContent.set(text);
          this.loadingDocument.set(false);
          // После загрузки текста настраиваем якорные ссылки
          setTimeout(() => {
            this.setupAnchorLinks();
          }, 100);
        },
        error: (error) => {
          console.error('Error loading file content:', error);
          this.loadingDocument.set(false);
          this.fileContent.set(null);
        }
      });
  }

  downloadFile(doc: PublicSystemDocumentationDto): void {
    if (!doc.id) return;

    // Определяем язык для загрузки
    const langMap: Record<string, string> = {
      'en': 'en-US',
      'en-us': 'en-US',
      'en-US': 'en-US',
      'ru': 'ru-RU',
      'ru-ru': 'ru-RU',
      'ru-RU': 'ru-RU',
      'uz': 'uz-Latn-UZ',
      'uz-latn': 'uz-Latn-UZ',
      'uz-latn-uz': 'uz-Latn-UZ',
      'uz-Latn-UZ': 'uz-Latn-UZ'
    };
    const currentLang = this.translateService.currentLang || '';
    const normalizedLang = currentLang.toLowerCase();
    const langCode = langMap[currentLang] || langMap[normalizedLang] || 'uz-Latn-UZ';

    const apiUrl = `${environment.api}/common/SystemDocumentation/GetPublicFile/${doc.id}?language=${langCode}`;

    this.http.get(apiUrl, { responseType: 'blob' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = doc.fileName || 'document';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
        error: (error) => {
          console.error('Error downloading file:', error);
        }
      });
  }

  isImageFile(fileType: string | null): boolean {
    return fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png' || fileType === 'gif';
  }

  isPdfFile(fileType: string | null): boolean {
    return fileType === 'pdf';
  }

  isMarkdownFile(fileType: string | null): boolean {
    return fileType === 'md';
  }

  isTextFile(fileType: string | null): boolean {
    return fileType === 'txt';
  }

  canPreview(fileType: string | null): boolean {
    return this.isImageFile(fileType) || this.isPdfFile(fileType) || this.isMarkdownFile(fileType) || this.isTextFile(fileType);
  }

  toggleBlock(blockId: string | undefined): void {
    if (!blockId) return;
    const expanded = new Set(this.expandedBlocks());
    if (expanded.has(blockId)) {
      expanded.delete(blockId);
    } else {
      expanded.add(blockId);
    }
    this.expandedBlocks.set(expanded);
  }

  toggleChildBlock(blockId: string | undefined): void {
    if (!blockId) return;
    const expanded = new Set(this.expandedChildBlocks());
    if (expanded.has(blockId)) {
      expanded.delete(blockId);
    } else {
      expanded.add(blockId);
    }
    this.expandedChildBlocks.set(expanded);
  }

  isBlockExpanded(blockId: string | undefined): boolean {
    if (!blockId) return false;
    return this.expandedBlocks().has(blockId);
  }

  isChildBlockExpanded(blockId: string | undefined): boolean {
    if (!blockId) return false;
    return this.expandedChildBlocks().has(blockId);
  }
}
